/**
 * 우상단 종 아이콘 — 인앱 알림 진입점.
 *
 * UX:
 *   - 평소엔 unread 카운트 배지만 표시 (없으면 plain icon)
 *   - 클릭 시 Popover 로 최근 30건 노출
 *   - "모두 읽음" 버튼 / 개별 항목 클릭 시 읽음 처리
 *   - 60초마다 unread-count 폴링 (Popover 가 열린 동안에는 list 도 같이 refresh)
 *
 * 트레이드오프:
 *   - WebSocket 미사용 — 60초 polling 으로 충분 (사내 운영 도구 규모)
 *   - read-all 후 list 를 한 번 더 불러와서 즉시 reflect
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { AuditService, NotificationItem } from '../services/AuditService';
import { useNavigate } from 'react-router-dom';

const POLL_INTERVAL_MS = 60_000;

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return '방금 전';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}분 전`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}시간 전`;
  if (diff < 7 * 86400_000) return `${Math.floor(diff / 86400_000)}일 전`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

const ACTION_LABEL: Record<string, string> = {
  create: '등록',
  update: '수정',
  delete: '삭제',
  mention: '멘션',
};

const ACTION_COLOR: Record<string, 'success' | 'info' | 'error' | 'default' | 'secondary'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  mention: 'secondary',
};

const ENTITY_LABEL: Record<string, string> = {
  site_project: '현장',
  site_visit_log: '출장 일지',
  site_incident: '장애 이력',
  site_schedule: '일정',
};

const NotificationBell: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef<number | null>(null);
  const open = Boolean(anchorEl);

  const refreshUnread = useCallback(async () => {
    try {
      const n = await AuditService.unreadCount();
      setUnread(n);
    } catch {
      // silent — 폴링 실패는 무시
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await AuditService.listNotifications({ limit: 30 });
      setItems(resp.data);
      setUnread(resp.unread_count);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // 초기 1회 + 60초 폴링.
  useEffect(() => {
    refreshUnread();
    pollingRef.current = window.setInterval(refreshUnread, POLL_INTERVAL_MS);
    return () => {
      if (pollingRef.current != null) window.clearInterval(pollingRef.current);
    };
  }, [refreshUnread]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    refreshList();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickItem = async (n: NotificationItem) => {
    // 1) 낙관적 read 처리 → list 갱신은 background
    if (!n.read_at) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      setUnread((u) => Math.max(0, u - 1));
      AuditService.markRead(n.id).catch(() => {
        // 실패 시 다음 refresh 에서 상태 복구됨
      });
    }
    // 2) 관련 페이지로 이동
    handleClose();
    if (n.site_project_id) {
      if (n.entity_type === 'site_visit_log') {
        navigate(`/project/${n.site_project_id}/journal`);
      } else if (n.entity_type === 'site_incident') {
        navigate(`/project/${n.site_project_id}/incidents`);
      } else if (n.entity_type === 'site_schedule') {
        navigate(`/project/${n.site_project_id}/schedules`);
      } else if (n.entity_type === 'site_project') {
        navigate(`/project/${n.site_project_id}`);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await AuditService.markAllRead();
      setItems((prev) => prev.map((x) => (x.read_at ? x : { ...x, read_at: new Date().toISOString() })));
      setUnread(0);
    } catch {
      // silent
    }
  };

  const hasUnread = unread > 0;
  const BellIcon = hasUnread ? NotificationsActiveIcon : NotificationsNoneIcon;

  const list = useMemo(() => items, [items]);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{
          color: hasUnread ? 'primary.main' : 'text.secondary',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
        }}
        aria-label="알림"
      >
        <Badge badgeContent={unread} color="error" max={99}>
          <BellIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { mt: 1, width: 380, maxHeight: 540, borderRadius: 2, overflow: 'hidden' },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            알림
          </Typography>
          {unread > 0 && (
            <Chip size="small" color="primary" label={`${unread} unread`} />
          )}
          <Button
            size="small"
            startIcon={<DoneAllIcon fontSize="small" />}
            onClick={handleMarkAllRead}
            disabled={unread === 0}
            sx={{ textTransform: 'none' }}
          >
            모두 읽음
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : list.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                아직 알림이 없습니다.
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {list.map((n, idx) => {
                const actionLabel = ACTION_LABEL[n.action] ?? n.action;
                const actionColor = ACTION_COLOR[n.action] ?? 'default';
                const entityLabel = ENTITY_LABEL[n.entity_type] ?? n.entity_type;
                const isUnread = !n.read_at;
                const isMention = n.action === 'mention';
                return (
                  <React.Fragment key={n.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItemButton
                      onClick={() => handleClickItem(n)}
                      sx={{
                        py: 1,
                        px: 2,
                        // 멘션 알림은 좌측 강조선으로 일반 broadcast 와 구분.
                        borderLeft: isMention ? '3px solid' : '3px solid transparent',
                        borderLeftColor: isMention ? 'secondary.main' : 'transparent',
                        bgcolor: isMention
                          ? alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.14 : 0.07)
                          : isUnread
                            ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
                            : 'transparent',
                        '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.08) },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                            <Chip
                              size="small"
                              label={entityLabel}
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.68rem' }}
                            />
                            <Chip
                              size="small"
                              label={actionLabel}
                              color={actionColor}
                              sx={{ height: 18, fontSize: '0.68rem' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                              {formatRelative(n.occurred_at)}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isUnread ? 600 : 400,
                                color: 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {n.summary}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {n.actor_name}
                            </Typography>
                          </>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
